import React from "react";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { Reveal, SectionHeader } from "../components/site/Reveal";
import { PriceEstimator } from "../components/site/PriceEstimator";
import {
  DETAIL_PACKAGES, INTERIOR_PACKAGES, EXTERIOR_PACKAGES,
  INTERIOR_ADDONS, EXTERIOR_ADDONS, VEHICLE_SIZES, POLICIES,
} from "../lib/content";

const PackageCard = ({ p, highlight }) => (
  <div data-testid={`pkg-${p.id}`} className={`p-7 h-full flex flex-col transition-all duration-300 ${highlight ? "glass-card silver-glow" : "matte hover:border-white/15"}`}>
    {p.recommended && <span className="inline-block bg-silver text-black text-[9px] font-bold uppercase tracking-widest px-2 py-1 mb-3 self-start">Recommended for First-Time Customers</span>}
    <h3 id={p.id} className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight">{p.name}</h3>
    {p.tagline && <p className="text-gray-400 text-sm font-light mt-3 leading-relaxed">{p.tagline}</p>}
    <div className="flex items-end justify-between mt-4">
      <div>
        <span className="overline">From</span>
        <p className="font-mono-tech text-3xl font-semibold text-white">${p.base}</p>
      </div>
      {p.duration && <span className="font-mono-tech text-xs text-gray-500">{p.duration}</span>}
    </div>
    <div className="h-px bg-white/10 my-6" />
    <ul className="space-y-3 flex-1">
      {p.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300 font-light">
          <Check size={15} className="text-silver mt-0.5 shrink-0" /> {f}
        </li>
      ))}
    </ul>
    {p.note && <p className="text-xs text-gray-500 italic mt-5">{p.note}</p>}
    <Link to="/contact" state={{ estimate: { pkg: p.name } }} className="mt-7 w-full inline-block text-center bg-transparent text-white py-3.5 border border-white/20 hover:border-white hover:bg-white/5 transition-all uppercase tracking-widest text-xs font-bold">
      Book This
    </Link>
  </div>
);

export default function Services() {
  return (
    <>
      <Seo title="Services" description="Full detailing, interior, exterior and paint correction services in Noble Park, Melbourne. Mobile and in-house options." path="/services" />
      <section className="pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader overline="Services" title="Every service, every finish." description="Choose from maintenance, signature and ultimate full-detail packages — or standalone interior, exterior and paint correction services." />
        </div>
      </section>

      {/* Full detailing */}
      <section data-testid="full-detail" className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal><h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Full Detailing Packages</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {DETAIL_PACKAGES.map((p) => <Reveal key={p.id}><PackageCard p={p} highlight={p.recommended} /></Reveal>)}
          </div>
        </div>
      </section>

      {/* Interior */}
      <section id="interior" className="py-20 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal><h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Interior Detailing</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
            {INTERIOR_PACKAGES.map((p) => <Reveal key={p.id}><PackageCard p={p} /></Reveal>)}
          </div>
          <p className="text-gray-500 text-sm font-light italic mt-8">Note: we offer professional seat surface cleaning but do not provide extraction or deep wet-shampoo services.</p>
        </div>
      </section>

      {/* Exterior */}
      <section id="exterior" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal><h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Exterior Detailing</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
            {EXTERIOR_PACKAGES.map((p) => <Reveal key={p.id}><PackageCard p={p} /></Reveal>)}
          </div>
        </div>
      </section>

      {/* Vehicle sizes */}
      <section className="py-20 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal><h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Vehicle Size Pricing</h2></Reveal>
          <p className="text-gray-400 font-light mt-3 max-w-2xl">Regular detailing services use base pricing plus a vehicle-size modifier. Ceramic coating packages use their own fixed vehicle-size pricing.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {VEHICLE_SIZES.map((s) => (
              <div key={s.id} data-testid={`size-${s.id}`} className="matte p-6">
                <p className="font-display font-black text-xl text-white">{s.label}</p>
                <p className="text-gray-500 text-sm mt-1">{s.description}</p>
                <p className="font-mono-tech text-silver text-lg mt-4">{s.modifier === 0 ? "Base Price" : `Base + $${s.modifier}`}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price estimator */}
      <section id="estimator" className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <SectionHeader overline="Price Estimator" title="Get an instant estimate." description="Choose your vehicle size, package and add-ons for an estimated starting total. Final pricing depends on vehicle condition and inspection." />
          <div className="mt-10">
            <PriceEstimator defaultPackage="signature" />
          </div>
        </div>
      </section>

      {/* Add-ons listings */}
      <section id="addons" className="py-20 bg-[#0A0A0A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <Reveal><h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">Interior Add-Ons</h3></Reveal>
            <div className="mt-6 divide-y divide-white/10 matte">
              {INTERIOR_ADDONS.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-6 px-5 py-4">
                  <span className="text-gray-200 text-sm">{a.name}</span>
                  <span className="font-mono-tech text-silver text-sm shrink-0">{a.display}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Reveal><h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">Exterior Add-Ons</h3></Reveal>
            <div className="mt-6 divide-y divide-white/10 matte">
              {EXTERIOR_ADDONS.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-6 px-5 py-4">
                  <span className="text-gray-200 text-sm">{a.name}</span>
                  <span className="font-mono-tech text-silver text-sm shrink-0">{a.display}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Policies */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <SectionHeader overline="Policies" title="Booking & service policies." />
          <ul className="mt-10 space-y-3 text-gray-400 font-light text-sm md:text-base max-w-3xl">
            {POLICIES.map((p, i) => (<li key={i} className="flex gap-3"><span className="text-silver">·</span>{p}</li>))}
          </ul>
        </div>
      </section>
    </>
  );
}
